<template>
  <a
    class="promo-card"
    :href="promoCard.targetUrl"
    v-data-test-id="'promo-card'"
    @click="trackClick"
  >
    <ImageContentful
      :src="promoCard.imageUrl"
      containerSelector=".promo-card__image"
      :isBackgroundImage="true"
      :options="{ setHeight: false }"
      class="promo-card__image"
    />
    <div class="promo-card__content">
      <div class="promo-card__text" :class="'promo-card__text--' + promoCard.variant">
        {{ promoCard.text }}
      </div>
      <a v-if="isMobile" class="promo-card__link" :class="'promo-card__link--' + promoCard.variant">
        {{ promoCard.ctaText }}
      </a>
      <button v-else class="promo-card__cta" :class="'promo-card__cta--' + promoCard.variant">
        {{ promoCard.ctaText }}
      </button>
    </div>
  </a>
</template>

<script>
import { mapGetters } from 'vuex'
import { CONTEXTS } from '@/services/external-communication/external-communication'
import { observeIntersection } from '@/helpers/intersection-observer'
import ImageContentful from '@/components/_shared/ImageContentful.vue'

export default {
  name: 'PromoCard',
  components: { ImageContentful },

  props: {
    promoCard: {
      type: Object,
      required: true,
    },

    index: {
      type: Number,
      required: true,
    },

    onCardView: {
      type: Function,
    },

    onCardClick: {
      type: Function,
      default() {
        return () => {}
      },
    },
  },

  computed: {
    ...mapGetters({
      currentBreakpoint: 'filter/currentBreakpoint',
      isMqMedium: 'viewport/isMqMedium',
    }),

    position() {
      return Number(this.currentBreakpoint?.promoPositions?.indexOf(this.index)) + 1
    },

    isMobile() {
      return !this.isMqMedium
    },
  },

  mounted() {
    if (this.onCardView) this.addIntersectionObserver()
  },

  methods: {
    addIntersectionObserver() {
      if (!window.IntersectionObserver) return

      const onIntersection = () => this.onCardView(this.promoCard, this.position)
      observeIntersection(this.$el, onIntersection, { threshold: 0.7 })
    },

    trackClick() {
      this.onCardClick(this.promoCard, this.position)

      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.promotionClick,
        promotions: [
          {
            name: this.promoCard.identifier,
            creative: this.promoCard.imageUrl,
            position: CONTEXTS.promoCard,
          },
        ],
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/components/_shared/Cards/card-settings.scss';

.promo-card {
  @include card-basic;
  min-height: 250px;
  position: relative;

  @media ($mq-small) {
    min-height: 324px;
  }

  @media ($mq-medium) {
    min-height: 315px;
  }

  @media ($mq-large) {
    min-height: 521px;
  }

  @media (min-width: 1000px) {
    min-height: 493px;
  }

  @media (min-width: 1280px) {
    min-height: 485px;
  }

  @media (min-width: 1440px) {
    min-height: 522px;
  }

  @media (min-width: 1770px) {
    min-height: 508px;
  }

  &:hover {
    .promo-card__cta--dark {
      background-color: $color-grey-dark-1;
    }

    .promo-card__cta--light {
      background-color: rgba($color-white, 0.85);
    }

    .promo-card__cta--transparent {
      background-color: rgba($color-white, 0.1);
    }
  }

  &__image {
    flex: 1;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
  }

  &__content {
    position: absolute;
    bottom: 0;
  }

  &__text {
    margin: 0 20px;
    font-family: $font-family-helvetica-now;
    font-size: $f-size-large;
    font-weight: bold;
    color: $c-grey-dark-2;
    line-height: 1.2;
    letter-spacing: -0.01666667em;

    &--light,
    &--transparent {
      color: $color-white;
    }

    @media ($mq-medium) {
      margin: 0 32px;
      font-size: 34px;
    }
  }

  &__link {
    display: block;
    padding: 10px 20px 20px;
    font-size: $f-size-xsmall;
    font-weight: bold;
    line-height: $line-height;
    color: $color-white;
    text-decoration: underline;

    &--dark {
      color: $c-grey-dark-2;
    }
  }

  &__cta {
    margin: 24px 32px 32px;
    padding: 17px 24px 16px;
    font-size: $f-size-normal;
    line-height: 1;
    color: $color-white;
    background-color: $c-grey-dark-2;
    transition: background-color $transition-time ease-in-out;

    &--transparent {
      background-color: transparent;
      border: 2px solid $color-white;
    }

    &--light {
      color: $c-grey-dark-2;
      background-color: $color-white;
    }
  }
}
</style>
